'use strict';
import DomElementBuilder from '../../sxQuery/DomElementBuilder';
/** Shared DOM builder (used across the plugin) — keeps element creation consistent. */
const createEl = DomElementBuilder.createElement;
/**
 * Chromeless avatar overlay: a fixed, transparent, **click-through** flex row
 * `[ avatar column ][ subtitle panel ]`, sized/positioned per `avatarConfig`.
 * By default it shows only the avatar (no subtitles, no chat). A small floating
 * **settings** button opens a menu where the user can enable **Subtitles** (a
 * panel to the right of the avatar) and/or the **Chat** (docked below). All
 * user-facing strings come from the resolved (translated) labels.
 *
 * Built with the shared `DomElementBuilder`. Only the few nodes touched after
 * construction are kept as members; transient nodes stay local. Visibility of
 * the settings menu / subtitles / chat is driven by modifier classes on the
 * root, so the menu and chat input don't need to be held as members.
 */
class AvatarShell {
    constructor(config, opts = {}) {
        // User preferences (both OFF by default).
        this.subtitlesEnabled = false;
        this.chatEnabled = false;
        this.inputMode = 'voice';
        this.sheetCollapsed = false;
        this.settingsOpen = false;
        this.subtitleText = '';
        this.outsideClickHandler = null;
        this.pendingTimer = null;
        /**
         * Insert or update a message. `stream` reveals the text at a steady rate instead of showing each
         * delta the moment it lands.
         *
         * The model emits transcript deltas in uneven bursts - a few words, then a pause, then a whole
         * clause - so painting them directly makes the text lurch. Holding the target text and revealing it
         * on the frame clock gives the even, continuous typing of a modern chat, and it self-corrects: the
         * rate is derived from how much text is waiting, so it catches up on a burst instead of falling
         * behind. The final text is always exactly what arrived; only its timing is smoothed.
         */
        this.streams = new Map();
        /**
         * Where a stream gets its clock. `'speech'` follows the avatar's voice, so the words on screen are
         * the words being said. `'typing'` reveals at a steady reading rate and exists as the fallback for
         * a speech stream whose audio never arrives - nothing is typed out by choice any more.
         */
        this.speechClock = null;
        this.streamTimer = null;
        this.scrollSettled = true;
        /**
         * A question that arrived before the avatar had said anything, held until it has.
         *
         * On a fresh session the first search result lands before the intro transcript does, so rendering
         * immediately would put the chips above the greeting. Holding exactly one question is enough: the
         * race only exists at the very start.
         */
        this.pendingQuestion = null;
        this.opts = opts;
        this.labels = config.labels;
        this.root = this.build(config);
    }
    build(config) {
        const C = 'ss360-avatar';
        return config.layout === 'chat' ? this.buildChatLayout(C, config) : this.buildStageLayout(C, config);
    }
    /**
     * `layout: 'chat'` - one conversation panel. The avatar becomes a round bust breaking the panel's
     * top corner, the transcript fills the body, and the composer sits in the foot.
     *
     * Deliberately reuses every member the stage layout assigns (`stageEl`, `loadingEl`, `errorEl`,
     * `chatMessagesEl`, `subtitleTextEl`), so the whole public surface - `getStage()`, `showError()`,
     * `upsertMessage()`, `setSubtitle()` - keeps working without the widget knowing which layout it got.
     */
    buildChatLayout(C, config) {
        this.stageEl = createEl({ tag: 'div', classList: [`${C}__stage`] });
        this.loadingEl = createEl({
            tag: 'div',
            classList: [`${C}__loading`],
            children: [createEl({ tag: 'div', classList: [`${C}__spinner`] })]
        });
        this.errorEl = createEl({ tag: 'div', classList: [`${C}__error`], style: { display: 'none' } });
        // The bust hosts whichever renderer the avatar record selects - the renderer still paints into
        // `stageEl` via getStage(), it is simply a small square container now.
        const bust = createEl({
            tag: 'div',
            classList: [`${C}__bust`],
            children: [this.stageEl, this.loadingEl, this.errorEl]
        });
        this.chatMessagesEl = createEl({
            tag: 'div',
            classList: [`${C}__conv`],
            attrs: { 'aria-live': 'polite' }
        });
        this.listeningEl = createEl({
            tag: 'div',
            classList: [`${C}__listening`],
            children: [
                createEl({
                    tag: 'span',
                    classList: [`${C}__level`],
                    attrs: { 'aria-hidden': 'true' },
                    children: [0, 1, 2, 3, 4].map(() => createEl({ tag: 'span', classList: [`${C}__level-bar`] }))
                }),
                createEl({ tag: 'span', textContent: this.labels.listening })
            ]
        });
        const foot = createEl({
            tag: 'div',
            classList: [`${C}__panel-foot`],
            children: [this.listeningEl, this.buildComposer(C), this.buildInputSwitch(C)]
        });
        const panel = createEl({
            tag: 'div',
            classList: [`${C}__panel`],
            children: [this.buildSheetHandle(C), bust, this.chatMessagesEl, foot]
        });
        // Subtitles have no place in the panel (the transcript IS the subtitles), but the node is still
        // created and left detached so setSubtitle() stays a no-op instead of a crash.
        this.subtitleTextEl = createEl({ tag: 'div', classList: [`${C}__subtitles-text`], style: { display: 'none' } });
        return createEl({
            tag: 'div',
            classList: [C, `${C}--layout-chat`, `${C}--size-${config.size}`, `${C}--pos-${config.position}`],
            attrs: { role: 'region', 'aria-label': this.labels.ariaLabel },
            children: [panel]
        });
    }
    /** The original presentation: fixed avatar viewport, optional subtitle panel and docked chat. */
    buildStageLayout(C, config) {
        // Avatar viewport (fixed height). The renderer clears only the inner
        // `stage`, so overlays/settings live in this wrapper and survive.
        this.stageEl = createEl({ tag: 'div', classList: [`${C}__stage`] });
        this.loadingEl = createEl({
            tag: 'div',
            classList: [`${C}__loading`],
            children: [createEl({ tag: 'div', classList: [`${C}__spinner`] })]
        });
        this.errorEl = createEl({ tag: 'div', classList: [`${C}__error`], style: { display: 'none' } });
        const stageWrap = createEl({
            tag: 'div',
            classList: [`${C}__stage-wrap`],
            children: [this.stageEl, this.loadingEl, this.errorEl, this.buildSettings(C)]
        });
        const main = createEl({ tag: 'div', classList: [`${C}__main`], children: [stageWrap, this.buildChat(C)] });
        // Subtitles render in a panel to the RIGHT of the avatar (not over it);
        // enabling reserves its width so the avatar shifts over.
        this.subtitleTextEl = createEl({
            tag: 'div',
            classList: [`${C}__subtitles-text`],
            attrs: { 'aria-live': 'polite' },
            style: { display: 'none' }
        });
        const subtitles = createEl({ tag: 'div', classList: [`${C}__subtitles`], children: [this.subtitleTextEl] });
        return createEl({
            tag: 'div',
            classList: [C, `${C}--size-${config.size}`, `${C}--pos-${config.position}`],
            attrs: { role: 'img', 'aria-label': this.labels.ariaLabel },
            children: [main, subtitles]
        });
    }
    buildChat(C) {
        this.chatMessagesEl = createEl({ tag: 'div', classList: [`${C}__chat-messages`], attrs: { 'aria-live': 'polite' } });
        return createEl({ tag: 'div', classList: [`${C}__chat`], children: [this.chatMessagesEl, this.buildComposer(C)] });
    }
    /**
     * Segmented microphone / keyboard control for `layout: 'chat'`.
     *
     * Replaces the Chat toggle buried in the settings menu: how you talk to the assistant is the panel's
     * primary choice, not a setting. Both halves are real buttons carrying `aria-pressed`, so the pair
     * reads and operates as a two-state control from the keyboard.
     */
    buildInputSwitch(C) {
        const mic = createEl({
            tag: 'button',
            classList: [`${C}__mode`, `${C}__mode--voice`],
            attrs: { type: 'button', 'aria-pressed': 'true', 'aria-label': this.labels.voiceInput, title: this.labels.voiceInput },
            html: MIC_ICON
        });
        const keyboard = createEl({
            tag: 'button',
            classList: [`${C}__mode`, `${C}__mode--text`],
            attrs: { type: 'button', 'aria-pressed': 'false', 'aria-label': this.labels.textInput, title: this.labels.textInput },
            html: KEYBOARD_ICON
        });
        mic.addEventListener('click', () => this.requestInputMode('voice'));
        keyboard.addEventListener('click', () => this.requestInputMode('text'));
        return createEl({ tag: 'div', classList: [`${C}__switch`], children: [mic, keyboard] });
    }
    /**
     * Grab handle for the bottom sheet the panel becomes on small screens.
     *
     * Hidden on wide viewports, where the panel floats and there is nothing to get out of the way of.
     * On a phone the sheet takes ~70% of the height, so without this the conversation would permanently
     * cover the products it is narrowing - which is the opposite of what moving the questions into the
     * panel was for.
     */
    buildSheetHandle(C) {
        const handle = createEl({
            tag: 'button',
            classList: [`${C}__handle`],
            attrs: {
                type: 'button',
                'aria-expanded': 'true',
                'aria-label': this.labels.toggleConversation,
                title: this.labels.toggleConversation
            },
            children: [createEl({ tag: 'span', classList: [`${C}__handle-grip`], attrs: { 'aria-hidden': 'true' } })]
        });
        handle.addEventListener('click', () => this.setSheetCollapsed(!this.sheetCollapsed));
        return handle;
    }
    /** Collapse the sheet to its foot, or restore it. No-op visually on wide viewports. */
    setSheetCollapsed(collapsed) {
        this.sheetCollapsed = collapsed;
        const C = 'ss360-avatar';
        this.root.classList.toggle(`${C}--sheet-collapsed`, collapsed);
        const handle = this.root.querySelector(`.${C}__handle`);
        if (handle) {
            handle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        }
        if (!collapsed) {
            this.chatMessagesEl.scrollTop = this.chatMessagesEl.scrollHeight;
        }
    }
    requestInputMode(mode) {
        if (this.inputMode === mode)
            return;
        this.setInputMode(mode);
        if (this.opts.onInputModeChange) {
            this.opts.onInputModeChange(mode);
        }
    }
    /**
     * Reflect the input mode in the UI. Separate from {@link requestInputMode} so the widget can set the
     * initial mode, or correct it when the microphone turns out to be unavailable, without looping back
     * through its own callback.
     */
    setInputMode(mode) {
        this.inputMode = mode;
        const C = 'ss360-avatar';
        this.root.classList.toggle(`${C}--input-text`, mode === 'text');
        const mic = this.root.querySelector(`.${C}__mode--voice`);
        const keyboard = this.root.querySelector(`.${C}__mode--text`);
        if (mic)
            mic.setAttribute('aria-pressed', mode === 'voice' ? 'true' : 'false');
        if (keyboard)
            keyboard.setAttribute('aria-pressed', mode === 'text' ? 'true' : 'false');
        if (mode === 'text') {
            this.setInputLevel(0);
            const input = this.root.querySelector(`.${C}__chat-input`);
            if (input) {
                try {
                    input.focus();
                }
                catch (e) { /* ignore */ }
            }
        }
    }
    getInputMode() {
        return this.inputMode;
    }
    /**
     * The customer is speaking (server VAD). The meter reads louder and brighter while it is on, so
     * 'I am being heard' is unmistakable rather than a few bars twitching.
     */
    setSpeaking(on) {
        this.root.classList.toggle('ss360-avatar--speaking', on);
    }
    /**
     * The customer has started talking. Their bubble appears immediately, in their own colour, with the
     * dots animating inside it - so the turn has a place on screen from the first word rather than a
     * separate 'transcribing' indicator somewhere else. The transcript then replaces the dots inside
     * that same bubble, which is why it reads as the block becoming the message.
     *
     * Idempotent: a pause mid-sentence raises speech_started again and must reuse the bubble already
     * standing, not add a second one.
     */
    beginUserTurn() {
        this.cancelPendingTimer();
        // Both of these belong to a previous turn: a thinking bubble that never got its reply, and a
        // reserved slot whose transcript never arrived. The reserved one matters most - it is hidden,
        // so reusing it would leave the customer talking with nothing on screen at all.
        this.removePlaceholder(AvatarShell.THINKING_ID);
        const existing = this.userPlaceholder();
        if (existing && existing.classList.contains(AvatarShell.CLS_PENDING)) {
            return; // a pause mid-sentence: the bubble is already standing
        }
        if (existing && existing.parentNode) {
            // Reserved, so hidden: a turn whose transcript never arrived. Reusing it would leave the
            // customer talking with nothing on screen.
            existing.parentNode.removeChild(existing);
        }
        this.chatMessagesEl.appendChild(this.buildPendingBubble(AvatarShell.PENDING_USER_ID, 'user'));
        this.scrollConversationToEnd();
    }
    /**
     * The customer has stopped and the reply is now what we are waiting for. The avatar's own bubble of
     * dots appears, and the timeout starts here rather than at the first word - a turn the screening
     * filter drops (a cough) must not leave either bubble behind for good.
     */
    beginAvatarWait() {
        this.cancelPendingTimer();
        if (!this.chatMessagesEl.querySelector(`[data-mid="${AvatarShell.THINKING_ID}"]`)) {
            this.chatMessagesEl.appendChild(this.buildPendingBubble(AvatarShell.THINKING_ID, 'avatar'));
        }
        this.scrollConversationToEnd();
        this.pendingTimer = window.setTimeout(() => this.cancelPendingTurn(), 8000);
    }
    removePlaceholder(id) {
        const node = this.chatMessagesEl.querySelector(`[data-mid="${id}"].${AvatarShell.CLS_PENDING}, [data-mid="${id}"].${AvatarShell.CLS_RESERVED}`);
        if (node && node.parentNode)
            node.parentNode.removeChild(node);
    }
    /** Remove whichever placeholders are still unfilled. */
    cancelPendingTurn() {
        this.cancelPendingTimer();
        const stale = this.chatMessagesEl.querySelectorAll(`.${AvatarShell.CLS_PENDING}, .${AvatarShell.CLS_RESERVED}`);
        Array.prototype.forEach.call(stale, (node) => {
            if (node.parentNode)
                node.parentNode.removeChild(node);
        });
    }
    /** The slot standing for the current customer turn, dots showing or reserved-and-hidden. */
    userPlaceholder() {
        return this.chatMessagesEl.querySelector(`[data-mid="${AvatarShell.PENDING_USER_ID}"].${AvatarShell.CLS_PENDING}, `
            + `[data-mid="${AvatarShell.PENDING_USER_ID}"].${AvatarShell.CLS_RESERVED}`);
    }
    cancelPendingTimer() {
        if (this.pendingTimer !== null) {
            window.clearTimeout(this.pendingTimer);
            this.pendingTimer = null;
        }
    }
    buildPendingBubble(id, sender) {
        const C = 'ss360-avatar';
        return createEl({
            tag: 'div',
            classList: [`${C}__msg`, `${C}__msg--${sender}`, `${C}__msg--pending`],
            attrs: { 'aria-busy': 'true' },
            data: { mid: id },
            children: [0, 1, 2].map(() => createEl({ tag: 'i', classList: [`${C}__dot`], attrs: { 'aria-hidden': 'true' } }))
        });
    }
    /** Drive the level meter from the mic's RMS (0..1). Cheap enough to call per audio frame. */
    setInputLevel(level) {
        if (!this.listeningEl)
            return;
        const clamped = Math.max(0, Math.min(1, level || 0));
        this.listeningEl.style.setProperty('--level', String(clamped));
    }
    /** The text composer - shared by both layouts, so there is one submit path, not two. */
    buildComposer(C) {
        const input = createEl({
            tag: 'input',
            classList: [`${C}__chat-input`],
            attrs: { type: 'text', placeholder: this.labels.messagePlaceholder, 'aria-label': this.labels.messagePlaceholder }
        });
        const send = createEl({
            tag: 'button',
            classList: [`${C}__chat-send`],
            attrs: { type: 'submit', 'aria-label': this.labels.send },
            html: SEND_ICON
        });
        const form = createEl({ tag: 'form', classList: [`${C}__chat-form`], children: [input, send] });
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            const value = input.value.trim();
            if (value && this.opts.onSendText) {
                this.opts.onSendText(value);
                input.value = '';
            }
        });
        return form;
    }
    buildSettings(C) {
        const btn = createEl({
            tag: 'button',
            classList: [`${C}__settings-btn`],
            attrs: { type: 'button', 'aria-label': this.labels.settings, 'aria-haspopup': 'true', 'aria-expanded': 'false' },
            html: GEAR_ICON
        });
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.toggleSettings(); });
        // Header: settings title (reuses the existing label) + a close (×) button.
        const closeBtn = createEl({
            tag: 'button',
            classList: [`${C}__settings-close`],
            attrs: { type: 'button', 'aria-label': 'Close' },
            html: CLOSE_ICON
        });
        closeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.toggleSettings(false); });
        const header = createEl({
            tag: 'div',
            classList: [`${C}__settings-header`],
            children: [
                createEl({ tag: 'span', classList: [`${C}__settings-title`], textContent: this.labels.settings }),
                closeBtn
            ]
        });
        const menu = createEl({
            tag: 'div',
            classList: [`${C}__settings-menu`],
            attrs: { role: 'menu' },
            children: [
                header,
                this.buildToggleRow(C, this.labels.subtitles, (on) => this.setSubtitlesEnabled(on)),
                this.buildToggleRow(C, this.labels.chat, (on) => this.setChatEnabled(on))
            ]
        });
        return createEl({ tag: 'div', classList: [`${C}__settings`], children: [btn, menu] });
    }
    buildToggleRow(C, label, onChange) {
        const input = createEl({ tag: 'input', classList: [`${C}__settings-check`], attrs: { type: 'checkbox' } });
        input.addEventListener('change', () => onChange(input.checked));
        const labelText = createEl({ tag: 'span', classList: [`${C}__settings-label`], textContent: label });
        return createEl({ tag: 'label', classList: [`${C}__settings-row`], children: [input, labelText] });
    }
    /** Append the overlay to the document body. */
    mount() {
        document.body.appendChild(this.root);
        this.setPhase('idle');
    }
    /** The element a renderer should paint into. */
    getStage() {
        return this.stageEl;
    }
    setPhase(phase) {
        this.root.setAttribute('data-phase', phase);
    }
    /** Fade the avatar stage in/out. */
    setStageVisible(visible) {
        this.stageEl.classList.toggle('ss360-avatar__stage--visible', visible);
    }
    showStageLoading(show) {
        this.loadingEl.style.display = show ? '' : 'none';
    }
    showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.style.display = '';
        this.showStageLoading(false);
        this.setPhase('error');
    }
    clearError() {
        this.errorEl.style.display = 'none';
        this.errorEl.textContent = '';
    }
    /** Set the latest subtitle text (only shown when subtitles are enabled). */
    setSubtitle(text) {
        this.subtitleText = text || '';
        this.updateSubtitleVisibility();
    }
    updateSubtitleVisibility() {
        const show = this.subtitlesEnabled && !!this.subtitleText;
        this.subtitleTextEl.textContent = show ? this.subtitleText : '';
        this.subtitleTextEl.style.display = show ? '' : 'none';
    }
    /** Supply the avatar's playback clock; without one, `'speech'` streams fall back to `'typing'`. */
    setSpeechClock(clock) {
        this.speechClock = clock;
    }
    pumpStreams() {
        let active = false;
        this.streams.forEach((stream, id) => {
            if (stream.shown >= stream.target.length) {
                this.streams.delete(id);
                return;
            }
            let allowed = this.allowance(stream);
            if (allowed <= stream.shown) {
                // Paced to the voice and the voice has not reached here yet: the text is waiting, not
                // finished, so the loop keeps running. Unless it has been waiting far too long - audio
                // that never starts (autoplay blocked, a worklet that failed) must not leave the reply
                // permanently half-written, so after STREAM_STALL_TICKS it reverts to plain typing.
                stream.stalled += 1;
                if (stream.stalled < AvatarShell.STREAM_STALL_TICKS) {
                    active = true;
                    return;
                }
                stream.pace = 'typing';
                allowed = this.allowance(stream);
            }
            stream.stalled = 0;
            const ceiling = Math.min(stream.target.length, allowed);
            const maxStep = AvatarShell.STREAM_MAX_CPS * AvatarShell.STREAM_TICK_MS / 1000;
            stream.shown = Math.min(ceiling, stream.shown + maxStep);
            stream.node.textContent = stream.target.slice(0, Math.floor(stream.shown));
            active = true;
        });
        if (active) {
            this.scrollSettled = false;
        }
        const scrolling = this.scrollSettled ? false : this.easeScroll();
        if (active || scrolling) {
            return;
        }
        this.stopStreaming();
    }
    /**
     * How many characters of this stream may be on screen at the end of this tick.
     *
     * For speech-paced text that is the fraction of the turn's audio already spoken, applied to the
     * text received so far and allowed to run slightly ahead. Both halves of the ratio grow while the
     * turn streams, so it self-corrects - no words-per-second guess - and when the model stops sending,
     * the played milliseconds catch up to the received ones and the last character lands with the last
     * sound. With no audio (text mode, or the voice not started yet) it falls back to typing, so the
     * text never simply stops.
     */
    allowance(stream) {
        const step = AvatarShell.STREAM_TYPING_CPS * AvatarShell.STREAM_TICK_MS / 1000;
        if (stream.pace !== 'speech' || !this.speechClock) {
            return stream.shown + step;
        }
        const { playedMs, receivedMs } = this.speechClock();
        if (receivedMs <= 0) {
            return stream.shown + step;
        }
        const spoken = Math.min(1, (playedMs / receivedMs) * AvatarShell.SPEECH_LEAD);
        return spoken * stream.target.length;
    }
    startStreaming() {
        if (this.streamTimer !== null)
            return;
        this.streamTimer = window.setInterval(() => this.pumpStreams(), AvatarShell.STREAM_TICK_MS);
    }
    stopStreaming() {
        if (this.streamTimer === null)
            return;
        window.clearInterval(this.streamTimer);
        this.streamTimer = null;
    }
    /** Show every in-flight message in full at once — used when the shell goes away. */
    snapStreams() {
        this.streams.forEach((stream) => { stream.node.textContent = stream.target; });
        this.streams.clear();
        this.stopStreaming();
    }
    /**
     * Ask for the conversation to end up at the bottom. It gets there on the tick loop, closing a fixed
     * fraction of the remaining distance each tick, so a new message pulls the view down in a glide
     * rather than a jump - and because the target is re-read every tick, text that keeps growing keeps
     * the glide going instead of restarting it.
     */
    scrollConversationToEnd() {
        this.scrollSettled = false;
        this.startStreaming();
    }
    /** @return true while there is still distance to cover. */
    easeScroll() {
        const el = this.chatMessagesEl;
        const target = el.scrollHeight - el.clientHeight;
        const remaining = target - el.scrollTop;
        if (Math.abs(remaining) < 1) {
            el.scrollTop = target;
            this.scrollSettled = true;
            return false;
        }
        el.scrollTop += remaining * AvatarShell.SCROLL_EASE;
        return true;
    }
    /**
     * Insert or update a chat message by id.
     *
     * `pace` chooses the reveal clock: `'speech'` for the avatar's own words, which follow its audio.
     * `false` sets the text at once and lets the bubble fade it in - the customer's own turns, spoken or
     * typed, because they already know what they said.
     */
    upsertMessage(msg, pace = false) {
        const stream = pace === true ? 'typing' : (pace === false ? null : pace);
        // The avatar's first real word ends both waits. Its own thinking bubble goes; the customer's
        // becomes a reserved slot - dots off, nothing drawn - because the input transcript is a separate
        // pass that routinely lands after the reply has started. Deleting it outright would put a late
        // transcript below the answer it prompted; leaving the dots spinning next to an avatar that is
        // already talking is what read as 'the dots are there in parallel'.
        if (msg.sender === 'avatar') {
            const thinking = this.chatMessagesEl.querySelector(`[data-mid="${AvatarShell.THINKING_ID}"].${AvatarShell.CLS_PENDING}`);
            if (thinking && thinking.parentNode)
                thinking.parentNode.removeChild(thinking);
            const waiting = this.chatMessagesEl.querySelector(`[data-mid="${AvatarShell.PENDING_USER_ID}"].${AvatarShell.CLS_PENDING}`);
            if (waiting) {
                waiting.classList.remove(AvatarShell.CLS_PENDING);
                waiting.classList.add(AvatarShell.CLS_RESERVED);
                waiting.removeAttribute('aria-busy');
                waiting.innerHTML = '';
            }
        }
        // The customer's message adopts whatever slot is standing for this turn, taking the node over
        // and re-keying it to its own id.
        //
        // The slot's id marks a place in the conversation; it is never a message's id. When the two were
        // the same, the next utterance's beginUserTurn() found the bubble now holding the previous
        // transcript, saw it was no longer pending, and removed it - quietly deleting what the customer
        // had just said from their own transcript.
        if (msg.sender === 'user' && !this.chatMessagesEl.querySelector(`[data-mid="${cssEscape(msg.id)}"]`)) {
            const slot = this.userPlaceholder();
            if (slot) {
                slot.setAttribute('data-mid', msg.id);
            }
        }
        let node = this.chatMessagesEl.querySelector(`[data-mid="${cssEscape(msg.id)}"]`);
        if (node && (node.classList.contains(AvatarShell.CLS_PENDING)
            || node.classList.contains(AvatarShell.CLS_RESERVED))) {
            node.classList.remove(AvatarShell.CLS_PENDING);
            node.classList.remove(AvatarShell.CLS_RESERVED);
            node.removeAttribute('aria-busy');
            node.innerHTML = '';
            // The dots become the words in the same bubble; the class carries a one-shot fade so the
            // change reads as the block resolving rather than a swap.
            node.classList.add(AvatarShell.CLS_REVEALED);
            this.cancelPendingTimer();
        }
        if (!node) {
            node = createEl({
                tag: 'div',
                classList: [`ss360-avatar__msg`, `ss360-avatar__msg--${msg.sender}`],
                data: { mid: msg.id }
            });
            this.chatMessagesEl.appendChild(node);
        }
        if (stream) {
            const existing = this.streams.get(msg.id);
            const shown = existing ? existing.shown : 0;
            // A shorter target means the turn restarted; drop back rather than freeze mid-sentence.
            this.streams.set(msg.id, {
                target: msg.content,
                shown: Math.min(shown, msg.content.length),
                node,
                pace: stream,
                stalled: 0
            });
            this.startStreaming();
        }
        else {
            this.streams.delete(msg.id);
            node.textContent = msg.content;
        }
        this.flushPendingQuestion();
        this.scrollConversationToEnd();
    }
    flushPendingQuestion() {
        const pending = this.pendingQuestion;
        if (!pending)
            return;
        this.pendingQuestion = null;
        this.renderQuestion(pending);
    }
    /**
     * Render a guided question's answers as chips in the conversation (`layout: 'chat'`).
     *
     * The question text is a compact label above the chips, not a message bubble. As a bubble it read
     * as the avatar saying the same thing twice; omitting it entirely was worse, because the model
     * chooses its own question to ask aloud while the chips always answer the first one the search
     * still has open - so the two genuinely diverge, and chips whose question is only in the audio are
     * unanswerable. A label reads as part of the control rather than as another utterance.
     *
     * An answered question is left alone - its group is the record of a turn already taken. One still
     * open on a later result is rebuilt at the foot of the conversation, because `onAnswer` closes over
     * the `SearchResponse` it was built from: answering through an older group would submit that
     * render's filters and sort, discarding whatever the customer changed since.
     */
    renderQuestion(question) {
        const C = 'ss360-avatar';
        const existing = this.chatMessagesEl.querySelector(`[data-qid="${cssEscape(question.id)}"]`);
        if (existing !== null && existing.getAttribute('data-answered') === 'true') {
            return;
        }
        // Nothing said yet: hold it, so the first question cannot land above the greeting. The newest
        // one wins, for the same reason as the rebuild above.
        if (!this.chatMessagesEl.querySelector(`.${C}__msg--avatar`)) {
            this.pendingQuestion = question;
            return;
        }
        // Every unanswered group goes, this question's own included: either the avatar moved on (old
        // chips would stack up at the bottom) or it is re-asking, and the group built below replaces it.
        const stale = this.chatMessagesEl.querySelectorAll(`.${C}__chips:not([data-answered="true"])`);
        Array.prototype.forEach.call(stale, (group) => {
            if (group.parentNode)
                group.parentNode.removeChild(group);
        });
        const group = createEl({
            tag: 'div',
            classList: [`${C}__chips`],
            attrs: { role: 'group', 'aria-label': question.text },
            data: { qid: question.id }
        });
        if (question.text) {
            group.appendChild(createEl({
                tag: 'div',
                classList: [`${C}__chips-label`],
                // aria-hidden: the group's aria-label already announces it, so a screen reader would
                // otherwise hear the question twice.
                attrs: { 'aria-hidden': 'true' },
                textContent: question.text
            }));
        }
        question.answers.forEach((answer, index) => {
            const chip = createEl({
                tag: 'button',
                // A null id is the deliberate skip answer - present, but visually quieter.
                classList: answer.id === null ? [`${C}__chip`, `${C}__chip--skip`] : [`${C}__chip`],
                attrs: { type: 'button' },
                textContent: answer.label
            });
            chip.addEventListener('click', () => this.pickAnswer(group, chip, question, answer, index));
            group.appendChild(chip);
        });
        this.chatMessagesEl.appendChild(group);
        this.chatMessagesEl.scrollTop = this.chatMessagesEl.scrollHeight;
    }
    /**
     * Lock the chosen chip and drop the rest. The chosen one stays as the record of that turn, which is
     * something the old answer band could never show: scrolling back reveals the path taken.
     */
    pickAnswer(group, chip, question, answer, index) {
        if (group.getAttribute('data-answered') === 'true') {
            return; // a second click while the next search is in flight
        }
        group.setAttribute('data-answered', 'true');
        Array.from(group.children).forEach((node) => {
            // The label stays: it is what makes the answered chip legible when scrolling back.
            if (node !== chip && !node.classList.contains('ss360-avatar__chips-label')) {
                group.removeChild(node);
            }
        });
        chip.classList.add('ss360-avatar__chip--picked');
        chip.setAttribute('aria-pressed', 'true');
        question.onAnswer(answer, index);
    }
    clearMessages() {
        this.chatMessagesEl.innerHTML = '';
    }
    // ── Settings menu (visibility driven by a root modifier class) ────────────
    toggleSettings(force) {
        this.settingsOpen = force === undefined ? !this.settingsOpen : force;
        this.root.classList.toggle('ss360-avatar--settings-open', this.settingsOpen);
        const btn = this.root.querySelector('.ss360-avatar__settings-btn');
        if (btn)
            btn.setAttribute('aria-expanded', this.settingsOpen ? 'true' : 'false');
        if (this.settingsOpen) {
            this.outsideClickHandler = (e) => {
                const target = e.target;
                if (!target || typeof target.closest !== 'function' || !target.closest('.ss360-avatar__settings')) {
                    this.toggleSettings(false);
                }
            };
            document.addEventListener('pointerdown', this.outsideClickHandler);
        }
        else if (this.outsideClickHandler) {
            document.removeEventListener('pointerdown', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
    }
    setSubtitlesEnabled(on) {
        this.subtitlesEnabled = on;
        this.root.classList.toggle('ss360-avatar--subtitles-open', on);
        this.updateSubtitleVisibility();
    }
    setChatEnabled(on) {
        this.chatEnabled = on;
        this.root.classList.toggle('ss360-avatar--chat-open', on);
        if (on) {
            const input = this.root.querySelector('.ss360-avatar__chat-input');
            if (input) {
                try {
                    input.focus();
                }
                catch (e) { /* ignore */ }
            }
        }
    }
    destroy() {
        this.snapStreams();
        this.cancelPendingTimer();
        if (this.outsideClickHandler) {
            document.removeEventListener('pointerdown', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.root.parentNode) {
            this.root.parentNode.removeChild(this.root);
        }
    }
}
AvatarShell.PENDING_USER_ID = 'user-pending';
AvatarShell.THINKING_ID = 'avatar-thinking';
AvatarShell.CLS_PENDING = 'ss360-avatar__msg--pending';
AvatarShell.CLS_RESERVED = 'ss360-avatar__msg--reserved';
AvatarShell.CLS_REVEALED = 'ss360-avatar__msg--revealed';
/**
 * Reveal cadence. A timer, deliberately, not requestAnimationFrame: rAF does not run when the page
 * is not being painted - a background tab, a minimised window, an unfocused embedded view - and it
 * was measured firing zero times in 500ms while `document.visibilityState` still said 'visible'.
 * With rAF as the clock the message text never appeared at all, which is far worse than text that
 * arrives in jumps. A timer is throttled in the background rather than stopped, so the text always
 * lands; 30fps is indistinguishable from 60 for type and does half the work.
 */
AvatarShell.STREAM_TICK_MS = 33;
/**
 * Typing rate, characters per second. Fast enough not to keep anyone waiting, slow enough that the
 * words visibly arrive one after another - the previous rule revealed any message in about ten
 * ticks whatever its length, which for a normal sentence is a third of a second and reads as
 * instant.
 */
AvatarShell.STREAM_TYPING_CPS = 45;
/**
 * How far ahead of the voice the avatar's text runs, as a multiple of the speech position. The text
 * reads slightly ahead of the ear, which is what feels natural; far ahead reads as two unrelated
 * things happening at once, which is what a whole paragraph over one spoken clause looks like.
 */
AvatarShell.SPEECH_LEAD = 1.5;
/**
 * Ceiling on how fast any stream may advance, characters per second.
 *
 * The audio clock sets the average rate, but it does not arrive smoothly: `playedMs` ticks along
 * continuously while the text it is measured against jumps each time a delta lands, so the
 * permitted position lurches forward and the writing visibly stutters. Capping the per-tick
 * advance turns each lurch into a short glide and costs nothing on average, because the cap is
 * well above the speaking rate it is smoothing.
 */
AvatarShell.STREAM_MAX_CPS = 70;
/** Ticks a speech-paced stream may make no progress before it gives up and types instead (~2s). */
AvatarShell.STREAM_STALL_TICKS = 60;
/** Fraction of the remaining distance covered per tick. 0.16 at 30fps is about a third of a second. */
AvatarShell.SCROLL_EASE = 0.16;
export default AvatarShell;
/** Minimal CSS attribute-value escaper for the `[data-mid="…"]` selector. */
function cssEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
}
/** Inline gear icon (currentColor) for the settings button. */
const GEAR_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M19.14 12.94a7.49 7.49 0 0 0 .05-.94 7.49 7.49 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.61-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.5.5 0 0 0-.61.22L2.2 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.03.31-.05.62-.05.94 0 .32.02.63.05.94L2.32 14.5a.5.5 0 0 0-.12.64l1.92 3.32c.13.24.42.32.61.22l2.39-.96c.49.38 1.03.7 1.62.94l.36 2.54c.05.24.25.42.5.42h3.84c.25 0 .45-.18.5-.42l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.24.1.48.02.61-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z"/></svg>';
/** Inline send (paper-plane) icon for the chat input. */
const SEND_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2z"/></svg>';
/** Inline microphone icon for the input switch. */
const MIC_ICON = '<svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor" aria-hidden="true"><path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.9V22h2v-3.1A7 7 0 0 0 19 12Z"/></svg>';
/** Inline keyboard icon for the input switch. */
const KEYBOARD_ICON = '<svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor" aria-hidden="true"><path d="M3 6h18v12H3V6Zm2 2v8h14V8H5Zm1.5 1.5h2v2h-2v-2Zm3.5 0h2v2h-2v-2Zm3.5 0h2v2h-2v-2Zm-7 3h11v2h-11v-2Z"/></svg>';
/** Inline close (×) icon for the settings popup. */
const CLOSE_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M18.3 5.71 12 12.01l-6.3-6.3-1.41 1.41 6.3 6.3-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.3z"/></svg>';
